import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import Text from "@components/Text";
import Tabs from "@components/Tabs";
import Button from "@components/Button";
import Spinner from "@components/Spinner";
import { getUser, updateUserSubscription, updateUserCameraInstalled, updateUserOptimizedForOpenings, updateUserOptimizedForWindrows, impersonateUser, getEmailDeliveries, AdminUserDetail, EmailDelivery } from "@services/adminService";
import { ROUTES } from "@stores/AccountStore";
import UserDevicesSubTab from "./UserDevicesSubTab";
import UserFieldsSubTab from "./UserFieldsSubTab";
import UserSettingsSubTab from "./UserSettingsSubTab";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";

const BackRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  cursor: pointer;
  user-select: none;
  &:hover span {
    text-decoration: underline;
  }
`;

const BackArrow = styled.span`
  font-size: 16px;
  color: #4a90d9;
`;

const BackText = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #4a90d9;
`;

const UserInfoCard = styled.div`
  background: #fafafa;
  border: 1px solid #ebeeef;
  border-radius: 8px;
  padding: 20px 24px;
  margin-bottom: 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 16px;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px 32px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const InfoField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const InfoLabel = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  color: #899093;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoValue = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  color: #181b1f;
  word-break: break-all;
`;

const InfoHint = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  line-height: 1.4;
  color: #899093;
`;

const CopyBtn = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  color: #4a90d9;
  cursor: pointer;
  user-select: none;
  &:hover {
    text-decoration: underline;
  }
`;

const ImpersonateRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
`;

const ImpersonateButton = styled(Button)`
  width: auto;
  min-width: 120px;
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const ErrorWrap = styled.div`
  text-align: center;
  padding: 48px 0;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 12px;
  flex-wrap: wrap;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    gap: 12px;
  }
`;

const StatBadge = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  color: #494c5b;
  background: #f0f1f3;
  border-radius: 12px;
  padding: 4px 12px;
`;

const MailCard = styled.div`
  background: #fafafa;
  border: 1px solid #ebeeef;
  border-radius: 8px;
  padding: 16px 24px;
  margin-bottom: 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 12px 16px;
  }
`;

const MailRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  padding: 8px 0;
  border-top: 1px solid #ebeeef;

  &:first-of-type {
    border-top: none;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    gap: 4px;
  }
`;

const MailSubject = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #181b1f;
  flex: 1;
  min-width: 0;
`;

const MailWhen = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  color: #899093;
  white-space: nowrap;
`;

const MailReason = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  color: #b45309;
  margin-top: 2px;
`;

const StatusPill = styled.span<{ tone: "good" | "bad" | "neutral" }>`
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 12px;
  padding: 3px 10px;
  white-space: nowrap;
  color: ${(p) => (p.tone === "good" ? "#3f6b0d" : p.tone === "bad" ? "#b3261e" : "#5c6470")};
  background: ${(p) =>
    p.tone === "good" ? "#eaf3e0" : p.tone === "bad" ? "#fbe9e7" : "#f0f1f3"};
`;

/**
 * `delivered` is the only genuinely good outcome; `sent` means Resend accepted
 * it but no receiving server has confirmed anything yet, which is exactly the
 * state that used to be mistaken for success.
 */
const eventTone = (event: string): "good" | "bad" | "neutral" =>
  event === "delivered" || event === "opened" || event === "clicked"
    ? "good"
    : event === "bounced" || event === "complained" || event === "failed"
      ? "bad"
      : "neutral";

const SUB_TABS = [
  { name: "Devices" },
  { name: "Fields" },
  { name: "Settings" },
];

interface Props {
  userId: string;
}

const AdminUserDetailPage: React.FC<Props> = ({ userId }) => {
  const navigate = useNavigate();
  const [userDetail, setUserDetail] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [savingSub, setSavingSub] = useState(false);
  const [savingCamera, setSavingCamera] = useState(false);
  const [savingOpenings, setSavingOpenings] = useState(false);
  const [savingWindrows, setSavingWindrows] = useState(false);
  const [deliveries, setDeliveries] = useState<EmailDelivery[] | null>(null);

  const loadUser = useCallback(() => {
    setLoading(true);
    setError(null);
    getUser(userId)
      .then(setUserDetail)
      .catch((e) => setError(e.message || "Failed to load user"))
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Mail history is keyed by ADDRESS, not user id, so it has to wait for the
  // user to load. A failure here must not blank the page — the delivery log is
  // supporting detail, and it is legitimately empty for anyone whose mail
  // predates the Resend webhook.
  useEffect(() => {
    const email = userDetail?.user.email;
    if (!email) return;
    let cancelled = false;
    getEmailDeliveries({ email, limit: 10 })
      .then((r) => !cancelled && setDeliveries(r.deliveries))
      .catch(() => !cancelled && setDeliveries([]));
    return () => {
      cancelled = true;
    };
  }, [userDetail?.user.email]);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopyFeedback(label);
      setTimeout(() => setCopyFeedback(null), 1500);
    });
  };

  const handleToggleSubscription = () => {
    if (!userDetail) return;
    const next = !userDetail.user.subscriptionPaid;
    setSavingSub(true);
    updateUserSubscription(userDetail.user.id, next)
      .then(() =>
        setUserDetail((prev) =>
          prev ? { ...prev, user: { ...prev.user, subscriptionPaid: next } } : prev
        )
      )
      .finally(() => setSavingSub(false));
  };

  const handleToggleCameraInstalled = () => {
    if (!userDetail) return;
    const next = !userDetail.user.cameraInstalled;
    setSavingCamera(true);
    updateUserCameraInstalled(userDetail.user.id, next)
      .then(() =>
        setUserDetail((prev) =>
          prev ? { ...prev, user: { ...prev.user, cameraInstalled: next } } : prev
        )
      )
      .finally(() => setSavingCamera(false));
  };

  const handleToggleOptimizedForOpenings = () => {
    if (!userDetail) return;
    const next = !userDetail.user.optimizedForOpenings;
    setSavingOpenings(true);
    updateUserOptimizedForOpenings(userDetail.user.id, next)
      .then(() =>
        setUserDetail((prev) =>
          prev ? { ...prev, user: { ...prev.user, optimizedForOpenings: next } } : prev
        )
      )
      .finally(() => setSavingOpenings(false));
  };

  const handleToggleOptimizedForWindrows = () => {
    if (!userDetail) return;
    const next = !userDetail.user.optimizedForWindrows;
    setSavingWindrows(true);
    updateUserOptimizedForWindrows(userDetail.user.id, next)
      .then(() =>
        setUserDetail((prev) =>
          prev ? { ...prev, user: { ...prev.user, optimizedForWindrows: next } } : prev
        )
      )
      .finally(() => setSavingWindrows(false));
  };

  const [impersonating, setImpersonating] = useState(false);

  const handleImpersonate = async () => {
    if (!userDetail) return;
    // Open the tab synchronously (inside the click gesture) so it isn't blocked
    // as a pop-up after the awaited request — mirrors the report-tab pattern.
    const tab = window.open("about:blank", "_blank");
    setImpersonating(true);
    try {
      await impersonateUser(userDetail.user.id); // audit + validation
      const url = `/fields?__impersonate=${encodeURIComponent(userDetail.user.id)}`;
      if (tab) tab.location.href = url;
      else window.open(url, "_blank"); // pop-up was blocked; try once more
    } catch (e) {
      if (tab) tab.close(); // backend refused (e.g. target is an admin)
    } finally {
      setImpersonating(false);
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Mail troubleshooting needs the clock time, not just the day — "sent 10:14,
  // delivered 10:14" vs "delayed for six minutes" is the whole question.
  const formatDateTime = (iso: string) =>
    new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  if (loading) {
    return (
      <LoadingWrap>
        <Spinner />
      </LoadingWrap>
    );
  }

  if (error || !userDetail) {
    return (
      <ErrorWrap>
        <Text size={14} color="#e74c3c">
          {error || "User not found"}
        </Text>
      </ErrorWrap>
    );
  }

  const { user, devices, fieldsCount, seasonsCount } = userDetail;

  return (
    <div>
      <BackRow onClick={() => navigate(ROUTES.ADMIN)}>
        <BackArrow>&larr;</BackArrow>
        <BackText>Back to users</BackText>
      </BackRow>

      <UserInfoCard>
        <InfoGrid>
          <InfoField>
            <InfoLabel>User ID</InfoLabel>
            <InfoValueRow>
              <InfoValue style={{ fontSize: 12, fontFamily: "monospace" }}>
                {user.id}
              </InfoValue>
              <CopyBtn onClick={() => copyToClipboard(user.id, "id")}>
                {copyFeedback === "id" ? "Copied!" : "Copy"}
              </CopyBtn>
            </InfoValueRow>
          </InfoField>

          <InfoField>
            <InfoLabel>E-mail</InfoLabel>
            <InfoValueRow>
              <InfoValue>{user.email}</InfoValue>
              <CopyBtn onClick={() => copyToClipboard(user.email, "email")}>
                {copyFeedback === "email" ? "Copied!" : "Copy"}
              </CopyBtn>
            </InfoValueRow>
          </InfoField>

          <InfoField>
            {/* Read-only here — the confirm/unconfirm toggle lives on the
                status pill in the users list (AdminUsersTab). */}
            <InfoLabel>Email status</InfoLabel>
            <InfoValue
              style={{
                color: user.emailVerified ? "#3f6b0d" : "#b45309",
                fontWeight: 600,
              }}
            >
              {user.emailVerified ? "Confirmed" : "Unconfirmed"}
            </InfoValue>
          </InfoField>

          <InfoField>
            <InfoLabel>Phone</InfoLabel>
            <InfoValue>{user.phoneNumber || "-"}</InfoValue>
          </InfoField>

          <InfoField>
            <InfoLabel>Name</InfoLabel>
            <InfoValue>{user.name || "-"}</InfoValue>
          </InfoField>

          <InfoField>
            <InfoLabel>Organization</InfoLabel>
            <InfoValue>{user.organizationName || "-"}</InfoValue>
          </InfoField>

          <InfoField>
            <InfoLabel>Created</InfoLabel>
            <InfoValue>{formatDate(user.createdAt)}</InfoValue>
          </InfoField>

          <InfoField>
            <InfoLabel>Subscription</InfoLabel>
            <InfoValueRow>
              <InfoValue
                style={{ color: user.subscriptionPaid ? "#3f6b0d" : "#b45309", fontWeight: 600 }}
              >
                {user.subscriptionPaid ? "Paid" : "Unpaid"}
              </InfoValue>
              <Button
                kind="outline"
                style={{ width: "auto", minWidth: 0, padding: "2px 10px", fontSize: 12 }}
                disabled={savingSub}
                onClick={handleToggleSubscription}
              >
                {savingSub
                  ? "Saving…"
                  : user.subscriptionPaid
                    ? "Mark unpaid"
                    : "Mark paid"}
              </Button>
            </InfoValueRow>
          </InfoField>

          <InfoField>
            <InfoLabel>Camera installed</InfoLabel>
            <InfoValueRow>
              <InfoValue
                style={{ color: user.cameraInstalled ? "#3f6b0d" : "#6b7280", fontWeight: 600 }}
              >
                {user.cameraInstalled ? "Yes" : "No"}
              </InfoValue>
              <Button
                kind="outline"
                style={{ width: "auto", minWidth: 0, padding: "2px 10px", fontSize: 12 }}
                disabled={savingCamera}
                onClick={handleToggleCameraInstalled}
              >
                {savingCamera
                  ? "Saving…"
                  : user.cameraInstalled
                    ? "Turn off"
                    : "Turn on"}
              </Button>
            </InfoValueRow>
          </InfoField>

          <InfoField>
            <InfoLabel>Optimized for openings</InfoLabel>
            <InfoValueRow>
              <InfoValue
                style={{
                  color: user.optimizedForOpenings ? "#3f6b0d" : "#6b7280",
                  fontWeight: 600,
                }}
              >
                {user.optimizedForOpenings ? "Yes" : "No"}
              </InfoValue>
              <Button
                kind="outline"
                style={{ width: "auto", minWidth: 0, padding: "2px 10px", fontSize: 12 }}
                disabled={savingOpenings}
                onClick={handleToggleOptimizedForOpenings}
              >
                {savingOpenings
                  ? "Saving…"
                  : user.optimizedForOpenings
                    ? "Turn off"
                    : "Turn on"}
              </Button>
            </InfoValueRow>
          </InfoField>

          <InfoField>
            <InfoLabel>Optimized for windrowers</InfoLabel>
            <InfoValueRow>
              <InfoValue
                style={{
                  color: user.optimizedForWindrows ? "#3f6b0d" : "#6b7280",
                  fontWeight: 600,
                }}
              >
                {user.optimizedForWindrows ? "Yes" : "No"}
              </InfoValue>
              <Button
                kind="outline"
                style={{ width: "auto", minWidth: 0, padding: "2px 10px", fontSize: 12 }}
                disabled={savingWindrows}
                onClick={handleToggleOptimizedForWindrows}
              >
                {savingWindrows
                  ? "Saving…"
                  : user.optimizedForWindrows
                    ? "Turn off"
                    : "Turn on"}
              </Button>
            </InfoValueRow>
            <InfoHint>
              Divides down yield where a windrower fed the harvester — one pass carrying
              two or more beds of crop while being credited with one swath of ground.
              Leave off unless this grower actually windrows; the detector reports what
              it finds either way.
            </InfoHint>
          </InfoField>
        </InfoGrid>

        <StatsRow>
          <StatBadge>
            {devices.filter((d) => !d.detachedAt).length} devices
          </StatBadge>
          <StatBadge>{fieldsCount} fields</StatBadge>
          <StatBadge>{seasonsCount} seasons</StatBadge>
        </StatsRow>

        <ImpersonateRow>
          <ImpersonateButton kind="outline" disabled={impersonating} onClick={handleImpersonate}>
            {impersonating ? "Opening…" : "Impersonate"}
          </ImpersonateButton>
        </ImpersonateRow>
      </UserInfoCard>

      {/* Delivery outcome of the mail we sent this address, from the Resend
          webhook. Answers "did their verification code actually arrive?"
          without opening the Resend dashboard. */}
      <MailCard>
        <InfoLabel>Email delivery</InfoLabel>
        {deliveries === null ? (
          <MailReason style={{ color: "#899093" }}>Loading…</MailReason>
        ) : deliveries.length === 0 ? (
          <MailReason style={{ color: "#899093" }}>
            No delivery events recorded for this address. Mail sent before the Resend
            webhook was connected has no history.
          </MailReason>
        ) : (
          <div style={{ marginTop: 8 }}>
            {deliveries.map((d) => (
              <MailRow key={d.resendId}>
                <StatusPill tone={eventTone(d.lastEvent)}>
                  {d.lastEvent.replace("_", " ")}
                </StatusPill>
                <MailSubject>
                  {d.subject || "(no subject)"}
                  {d.failureReason && <MailReason>{d.failureReason}</MailReason>}
                </MailSubject>
                <MailWhen>{formatDateTime(d.lastEventAt)}</MailWhen>
              </MailRow>
            ))}
          </div>
        )}
      </MailCard>

      <Tabs
        tabs={SUB_TABS}
        activeTab={activeTab}
        setActive={setActiveTab}
        style={{ marginBottom: 24 }}
      />

      {activeTab === 0 && (
        <UserDevicesSubTab userId={userId} devices={devices} onChanged={loadUser} />
      )}
      {activeTab === 1 && <UserFieldsSubTab userId={userId} />}
      {activeTab === 2 && <UserSettingsSubTab userId={userId} />}
    </div>
  );
};

export default AdminUserDetailPage;
